import {FlusiaIcon} from '../ui/FlusiaIcon';
import {useFluPilot} from '../../lib/onboarding/consultant/useFluPilot';
import { ClientOrderWorkflow } from '../clients/ClientOrderWorkflow';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MoreVertical, Edit, Briefcase, UserPlus } from 'lucide-react';
import LeadNotesSection from './LeadNotesSection';
import type { ComposerDrawerPayload, ComposerMode } from '../shared/timeline/InlineComposer';
import { CustomFieldsSection } from '../clients/CustomFieldsSection';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { useApp } from '../AppProvider';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import { useCurrentUser } from '../../lib/hooks/useCurrentUser';
import { ClientAvatar } from '../clients/ClientAvatar';
import { EnrichmentLeadCard } from '../enrichment/EnrichmentLeadCard';
import { EnrichmentProposalsSection } from '../enrichment/EnrichmentProposalsSection';
import { BETA_VOICE_AGENTS_ORG_IDS, canAccessBetaFeature } from '../../lib/config/betaOrgs';
import { LeadManagementRows } from './LeadManagementRows';
import { LeadClassificationRows } from './LeadClassificationRows';
import { LeadInfoRows } from './LeadInfoRows';
import { useOpenWhatsAppChat } from '../whatsapp-overlay/useOpenWhatsAppChat';
import { LeadCommentsSection } from './LeadCommentsSection';
import { LeadPeopleSection } from './LeadPeopleSection';
import { LeadNewsletterRow, LeadPipelineRow, LeadReferrerRow } from './LeadSingleRows';
import { ClientOriginCard } from '../marketing/ClientOriginCard';
import { PrivacyConsentCard } from './PrivacyConsentCard';
import { PanelSection, UserDot } from '../shared/property-rows';
// Task 3 "Partner drawer unificato": sezione property rows partner-specifiche.
// Task 1 "Tab Info stile Notion": tab Info montato via PartnerInfoTab (contenitore a
// sezioni toggle), che a sua volta monta Commissioni/Portal.
import { PartnerInfoExtras } from '../partners/PartnerInfoExtras';
import { PartnerInfoTab } from '../partners/info/PartnerInfoTab';
import { PartnerAgnosticInfoRows } from '../partners/PartnerAgnosticInfoRows';
// Task 4 "Partner drawer unificato": slot fase pipeline al posto dello status grid.
import { PartnerStageSlot } from '../partners/PartnerStageSlot';
import type { ExternalPartner } from '../../lib/types';
import type { PartnerExtras } from '../../pages/partners/usePartnerEntity';

interface LeadMobileLayoutProps {
  selectedClient: any;
  // Task 2 "Partner drawer unificato": tipo esteso dell'entità mostrata (include
  // 'partner'). Opzionale, non consumato qui — branch di rendering in Task 3.
  entityType?: 'lead' | 'client' | 'partner';
  isLead: boolean;
  showStatusEditor: string | null;
  setShowStatusEditor: (value: string | null) => void;
  showExpandedDetails: boolean;
  setShowExpandedDetails: (show: boolean) => void;
  shouldHideTags: boolean;
  leadStagesFromDb: any[];
  clientStateOptions: any[];
  availableUsers: any[];
  leadSources?: any[];
  notes: any[];
  activities: any[];
  notesLoading: boolean;
  t: any;
  formatDate: (date: string) => string;
  handleLeadUpdate: (id: string, updates: any) => Promise<boolean>;
  handleAddNote: () => void;
  handleAddTask: () => void;
  handleEditNote: (noteId: string, content: string) => Promise<void>;
  handleDeleteNote: (noteId: string) => Promise<void>;
  handleDeleteTask: (taskId: string, opts?: { skipGoogleSync?: boolean }) => Promise<void>;
  handleEditTask: (taskId: string, updates: any) => Promise<void>;
  handleUpdateActivities?: (activities: any[]) => void;
  /** Wiring del composer inline della cronologia (vedi `LeadNotesSection`). */
  composer?: {
    onSaved: () => void | Promise<void>;
    onOpenDrawer: (mode: ComposerMode, payload: ComposerDrawerPayload) => void;
  };
  showSkeleton: boolean;
  // Task 3 "Partner drawer unificato": entità partner grezza (richiesta dai manager
  // Commissioni/Portal) e campi extra per PartnerInfoExtras. Presenti solo quando
  // entityType === 'partner' (undefined altrimenti).
  partner?: ExternalPartner | null;
  partnerExtras?: PartnerExtras | null;
  // Props per modale
  isModal?: boolean;
  onClose?: () => void;
  onEditLead?: () => void;
  // Task 5 "Partner drawer unificato": callback dedicata alla modifica partner (apre
  // PartnerForm nel parent), gemella di `onEdit` già presente su LeadDesktopLayout/
  // LeadDetailsHeader (Task 4). Usata al posto di onEditLead quando entityType === 'partner'.
  onEdit?: () => void;
  onCreateDeal?: () => void;
  onConvertToClient?: () => void;
  isConverting?: boolean;
  detailsT?: any;
}

/**
 * Layout mobile della scheda lead/cliente sulle stesse righe di proprietà del
 * desktop (redesign Notion 13/08): Gestione, Classificazione, Informazioni con
 * edit inline, Commenti, Persone, righe Pipeline/Segnalatore/Origine/Privacy,
 * Altri campi. Le azioni trailing sono sempre visibili su touch (PropRow).
 * Il ramo partner mantiene le sue card (design congelato 12/08).
 */
export function LeadMobileLayout({
  selectedClient,
  entityType,
  isLead,
  shouldHideTags,
  leadStagesFromDb,
  clientStateOptions,
  availableUsers,
  notes,
  activities,
  notesLoading,
  t,
  formatDate,
  handleLeadUpdate,
  handleAddNote,
  handleAddTask,
  handleEditNote,
  handleDeleteNote,
  handleDeleteTask,
  handleEditTask,
  handleUpdateActivities,
  composer,
  showSkeleton,
  partner,
  partnerExtras,
  // Props modale
  isModal = false,
  onClose,
  onEditLead,
  onEdit,
  onCreateDeal,
  onConvertToClient,
  isConverting = false,
  detailsT,
}: LeadMobileLayoutProps) {
  const fluPilot=useFluPilot();
  const { hasPermission } = usePermissions();
  // Chat WhatsApp dal numero della riga: stesso hook e stesso menu della vista desktop.
  const { canWhatsApp, openChat: openWhatsAppChat, chooser: waChooser } = useOpenWhatsAppChat();
  const { toast } = useApp();
  const { client: clientLabel } = useMenuLabels();
  const { orgId } = useCurrentUser();
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      toast.success(`${label} copiato negli appunti`);
    });
  };
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });

  const canEdit = hasPermission(isLead ? 'leads' : 'clients', 'update');

  // Fase + assegnatario sempre visibili (barra di stato sotto l'header), anche
  // a "Gestione" collassata: stessa derivazione di LeadManagementRows (pipeline
  // vs lead_stage legacy), sola lettura qui — l'edit resta nella sezione.
  const currentStageId = selectedClient?.pipeline_id ? selectedClient?.pipeline_stage_id : selectedClient?.lead_stage;
  const currentStage = leadStagesFromDb?.find((s: any) => s.id === currentStageId);
  const currentAssignee = availableUsers?.find((u: any) => u.id === selectedClient?.assigned_to);

  // Chiudi menu azioni quando si clicca fuori
  useEffect(() => {
    if (!showActionsMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node) &&
        actionsButtonRef.current && !actionsButtonRef.current.contains(e.target as Node)
      ) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionsMenu]);

  const openActionsMenu = () => {
    if (actionsButtonRef.current) {
      const rect = actionsButtonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setShowActionsMenu(true);
  };

  return (
    <div className={`lg:hidden flex flex-col ${isModal ? 'h-full' : ''}`}>
      {/* Header mobile per modale */}
      {isModal && (
        <div data-flu-record-header={fluPilot?'true':undefined} className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2.5 flex-1 min-w-0 mr-3">
            <ClientAvatar client={selectedClient} size="sm" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {selectedClient.business_name}
              </h2>
              {selectedClient.email && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{selectedClient.email}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {fluPilot&&<button type="button" className="flu-header-trigger flu-record-trigger" aria-label="Flù, assistente AI" title="Parla con Flù" onClick={()=>window.dispatchEvent(new Event('flusia:flu-open'))}><FlusiaIcon name="flu" size={20}/></button>}
            {/* Menu azioni */}
            {(hasPermission(entityType === 'partner' ? 'external_partners' : (isLead ? 'leads' : 'clients'), 'update') || (entityType !== 'partner' && hasPermission('deals', 'create'))) && (
              <button
                ref={actionsButtonRef}
                onClick={openActionsMenu}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            )}
            {/* Pulsante chiudi */}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Barra di stato: fase + assegnatario sempre a vista, anche con "Gestione"
          collassata sotto. Sola lettura. */}
      {isModal && entityType !== 'partner' && (currentStage || currentAssignee) && (
        <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 text-xs overflow-hidden">
          {currentStage && (
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-200 flex-shrink-0">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: currentStage.color || '#9CA3AF' }} />
              {currentStage.name}
            </span>
          )}
          {currentAssignee && (
            <span className="inline-flex items-center gap-1.5 min-w-0 text-gray-500 dark:text-gray-400">
              <UserDot user={currentAssignee} />
              <span className="truncate">{currentAssignee.full_name || currentAssignee.email}</span>
            </span>
          )}
        </div>
      )}

      {/* Menu azioni (portal) */}
      {createPortal(
        showActionsMenu ? (
          <div
            ref={actionsMenuRef}
            className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-[10001]"
            style={{ top: menuPosition.top, right: menuPosition.right }}
          >
            {/* Task 5 "Partner drawer unificato": per un partner, permesso e callback
                dedicati — mai onEditLead (che nel parent apre ClientFormWizard su un
                cliente inesistente). */}
            {entityType === 'partner'
              ? hasPermission('external_partners', 'update') && onEdit && (
                  <button
                    onClick={() => { onEdit(); setShowActionsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifica Partner</span>
                  </button>
                )
              : canEdit && onEditLead && (
                  <button
                    onClick={() => { onEditLead(); setShowActionsMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifica</span>
                  </button>
                )}
            {/* Niente Crea offerta/Converti per un partner. */}
            {entityType !== 'partner' && hasPermission('deals', 'create') && onCreateDeal && (
              <button
                onClick={() => { onCreateDeal(); setShowActionsMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Briefcase className="w-4 h-4" />
                <span>Crea offerta</span>
              </button>
            )}
            {entityType !== 'partner' && isLead && hasPermission('clients', 'create') && onConvertToClient && (
              <button
                onClick={() => { onConvertToClient(); setShowActionsMenu(false); }}
                disabled={isConverting}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-green-600 dark:text-green-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <UserPlus className="w-4 h-4" />
                <span>{isConverting ? 'Conversione...' : `Converti in ${clientLabel.singular}`}</span>
              </button>
            )}
          </div>
        ) : null,
        document.body,
      )}

      {/* Zona informazioni: fissa, non scorre — si espande in loco quando si
          apre una sezione. max-h + scroll interno solo come rete di sicurezza
          se più sezioni sono aperte insieme (altrimenti spingerebbero via le
          Note invece di lasciarle sempre raggiungibili sotto). */}
      <div className={`flex-shrink-0 overflow-y-auto max-h-[50vh] ${isModal ? 'px-3 pt-3' : ''} space-y-3`}>
        {entityType === 'partner' ? (
          /* ===== Ramo PARTNER: card pre-redesign, design congelato 12/08 ===== */
          <>
            {selectedClient?.id && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-2">
                <PartnerStageSlot partnerId={selectedClient.id} onStageChanged={composer?.onSaved} />
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-3">
              <PartnerAgnosticInfoRows
                selectedClient={selectedClient}
                title={detailsT?.details?.entityInfo || 'Informazioni'}
                handleCopy={handleCopy}
              />
            </div>

            {partnerExtras && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-3">
                <PartnerInfoExtras extras={partnerExtras} />
              </div>
            )}

            {partner && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <PartnerInfoTab partner={partner} />
              </div>
            )}

            <LeadCommentsSection selectedClient={selectedClient} handleLeadUpdate={handleLeadUpdate} />
          </>
        ) : (
          /* ===== Lead/Cliente: stesse righe di proprietà del desktop, ma
             collassate dietro un PanelSection (invariato su desktop: qui è
             solo LeadMobileLayout) — la fase/assegnatario restano visibili
             nella barra di stato sopra anche a "Gestione" chiusa. ===== */
          <div className={isModal ? '' : 'px-3 pt-2'}>
            {/* Un'unica riga per tutto il blocco informativo: chiuso di
                default lascia solo questa riga sopra le Note; le sottosezioni
                dentro restano comunque singolarmente espandibili. */}
            <PanelSection title="Dettagli" defaultOpen={false}>
              <PanelSection title="Gestione" defaultOpen={false}>
                <LeadManagementRows
                  lead={selectedClient}
                  leadStages={leadStagesFromDb}
                  clientStateOptions={clientStateOptions}
                  availableUsers={availableUsers}
                  onLeadUpdate={handleLeadUpdate}
                  canEdit={canEdit}
                  isLead={isLead}
                  loading={showSkeleton}
                  hideHeader
                />
                <div className="mt-2">
                  <LeadClassificationRows selectedClient={selectedClient} shouldHideTags={shouldHideTags} canEdit={canEdit} onLeadUpdate={handleLeadUpdate} />
                </div>
              </PanelSection>

              <div className="mt-1">
                {waChooser}
                <LeadInfoRows
                  selectedClient={selectedClient}
                  canEdit={canEdit}
                  onLeadUpdate={handleLeadUpdate}
                  handleCopy={handleCopy}
                  onOpenWhatsApp={canWhatsApp ? (anchor, phone) => openWhatsAppChat(phone, anchor, null, selectedClient?.business_name) : undefined}
                  onEditWizard={onEditLead}
                  showVoiceRow={
                    !!selectedClient?.id &&
                    canAccessBetaFeature(BETA_VOICE_AGENTS_ORG_IDS, orgId) &&
                    (hasPermission('voice_agents', 'view') || hasPermission('voice_agents', 'dial'))
                  }
                  canDialVoice={hasPermission('voice_agents', 'dial')}
                  title={detailsT?.details?.entityInfo || 'Informazioni'}
                  defaultOpen={false}
                />
              </div>

              <ClientOrderWorkflow clientId={selectedClient.id} />

              {/* Persone (o Aziende) subito sotto Informazioni, come sul desktop.
                  Prima stava sotto Commenti ed era una sezione "Persone" dentro
                  un'altra sezione "Persone", perche' il componente ha gia' la sua. */}
              <LeadPeopleSection
                selectedClient={selectedClient}
                canEdit={canEdit}
                onLeadUpdate={handleLeadUpdate}
              />

              <PanelSection title="Commenti" defaultOpen={false}>
                <LeadCommentsSection selectedClient={selectedClient} handleLeadUpdate={handleLeadUpdate} />
              </PanelSection>

              <div className="mt-1">
                <LeadPipelineRow client={selectedClient} />
                <LeadReferrerRow client={selectedClient} />
                <LeadNewsletterRow client={selectedClient} canEdit={canEdit} onLeadUpdate={handleLeadUpdate} />
                {(selectedClient.ad_platform || selectedClient.utm_source || selectedClient.fbclid || selectedClient.gclid) && (
                  <ClientOriginCard client={selectedClient} />
                )}
                {selectedClient.privacy_consent_given && (
                  <PrivacyConsentCard client={selectedClient} />
                )}
              </div>

              {canEdit && selectedClient?.id && (
                <EnrichmentProposalsSection selectedClient={selectedClient} onLeadUpdate={handleLeadUpdate} />
              )}

              <PanelSection title="Altri campi">
                {hasPermission('lead_enrichment', 'view') && selectedClient?.id && (
                  <div className="px-1.5">
                    <EnrichmentLeadCard leadId={selectedClient.id} />
                  </div>
                )}
                <div className="px-1.5">
                  <CustomFieldsSection clientId={selectedClient.id} isEditing={false} loading={showSkeleton} />
                </div>
              </PanelSection>
            </PanelSection>
          </div>
        )}
      </div>

      {/* Note e Attività: unica parte che scorre, sempre visibile subito sotto
          la zona informazioni. min-h-0 è necessario in un flex column perché un
          figlio flex-1 non si restringe sotto il proprio contenuto di default:
          senza, la zona sopra spingerebbe le Note fuori dallo schermo invece di
          lasciarle scrollabili nello spazio restante.
          Lo scroll vero avviene DENTRO LeadNotesSection (il suo elenco è
          flex-1 overflow-y-auto quando isModal=true, stesso pattern già usato
          in LeadDesktopLayout) — qui NON mettiamo overflow-y-auto, solo il
          contenitore flex a altezza vincolata. pb-24 riserva lo spazio della
          bottom nav mobile fissa (z-[9999], ~96px sotto il drawer a z-[55]):
          senza, l'ultima nota resta coperta a fine scroll. Prima passavamo
          isModal a LeadNotesSection: senza, usava un fallback
          `calc(100vh-16rem)` pensato per pagina intera, tagliava le ultime
          righe indipendentemente da questo spazio riservato. */}
      <div className={`flex-1 min-h-0 flex flex-col ${isModal ? 'p-3 pb-24' : ''}`}>
        <LeadNotesSection
          lead={selectedClient}
          leadNotes={notes}
          leadActivities={activities}
          loadingNotes={notesLoading}
          translations={t}
          formatDate={formatDate}
          onAddNote={handleAddNote}
          onAddTask={handleAddTask}
          onEditNote={handleEditNote}
          onDeleteNote={handleDeleteNote}
          onDeleteActivity={handleDeleteTask}
          onEditActivity={handleEditTask}
          onUpdateActivities={handleUpdateActivities}
          composer={composer}
          loading={showSkeleton}
          entityType={entityType === 'partner' ? 'partner' : (isLead ? 'lead' : 'client')}
          isModal={isModal}
        />
      </div>
    </div>
  );
}
